import { useState, useCallback, useMemo, useEffect } from 'react';
import { Card } from '@/components/ui/cards/Card';
import { TABLE_PALETTE } from './Table.constants';
import { useTableColumns, useTableSort, useTableSelection } from '@/hooks/ui/table';
import { TableHeader } from './_TableHeader';
import { TableContainer, TableBody } from './_TableBody';
import { TableFooter } from './_TableFooter';
import { TableSkeleton } from './_TableSkeleton';
import type { TableProps, SortDirection } from './Table.types';

// Re-export types for convenience
export type {
  TableProps,
  TableColumn,
  TableAction,
  TableActionVariant,
  TablePagination,
  ColumnAlign,
  OverflowStrategy,
  SortDirection,
  ExpandableConfig,
  DetailField,
  BulkActionItem,
} from './Table.types';

/**
 * Advanced table component with sorting, pagination, selection, search, and actions
 * 
 * @example
 * ```tsx
 * <Table
 *   data={users}
 *   columns={[
 *     { key: 'name', header: 'Name', sortable: true },
 *     { key: 'email', header: 'Email' },
 *   ]}
 *   totalRows={100}
 *   searchable
 *   onSearch={handleSearch}
 *   pagination={{
 *     page: 1,
 *     pageSize: 10,
 *     total: 100,
 *     onChange: handlePageChange,
 *   }}
 * />
 * ```
 */
export function Table<T extends Record<string, any>>({
  columns,
  data,
  totalRows,
  pagination,
  searchable = true,
  searchPlaceholder = 'Buscar',
  onSearch,
  initialSearchValue,
  onEnterSearch,
  initialSort,
  onSortChange,
  serverSideSort = false,
  selectable = false,
  onSelectionChange,
  selectedRowIds,
  getRowId,
  className = '',
  style,
  'data-testid': dataTestId,
  isLoading = false,
  actions,
  actionsHeader,
  expandable,
  emptyMessage,
  hideTotalRows = false,
  minTableWidth: minTableWidthOverride,
  bulkActions,
  onSelectAllTotal,
  isSelectAllTotalActive = false,
  onClearSelectionTotal,
  selectAllTotalCount,
  isRowSelectable,
  getRowTextColor,
  footerNote,
  fontSize,
}: TableProps<T>) {
  // Auto-detect row ID function
  const chosenIdKey = useMemo(() => {
    if (!data || data.length === 0) return undefined as string | undefined;
    const candidates = ['id', 'uuid', '_id', 'key'];
    return candidates.find((key) =>
      data.every((row: any) => row?.[key] !== undefined && row?.[key] !== null),
    );
  }, [data]);

  const effectiveGetRowId = useMemo<NonNullable<TableProps<T>['getRowId']>>(() => {
    if (getRowId) return getRowId;
    if (chosenIdKey) {
      return (row: T) => (row as any)[chosenIdKey] as string | number;
    }
    return (row: T) => JSON.stringify(row);
  }, [getRowId, chosenIdKey]);

  // Local state for search and expanded rows. `initialSearchValue` only seeds this on mount —
  // it exists to re-sync the box after a `key`-driven remount of `Table` (see prop doc);
  // typing still flows through `onSearch`, the caller's real source of truth.
  const [searchValue, setSearchValue] = useState(initialSearchValue ?? '');
  const [expandedRowKeys, setExpandedRowKeys] = useState<Set<string | number>>(new Set());

  // Reset expanded rows when data changes (e.g., after filtering or pagination)
  useEffect(() => {
    setExpandedRowKeys(new Set());
  }, [data]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);
      onSearch?.(value);
    },
    [onSearch],
  );

  const toggleRowExpanded = useCallback((rowId: string | number) => {
    if (expandable?.accordion) {
      setExpandedRowKeys((prev) =>
        prev.has(rowId) ? new Set() : new Set([rowId]),
      );
    } else {
      setExpandedRowKeys((prev) => {
        const next = new Set(prev);
        if (next.has(rowId)) {
          next.delete(rowId);
        } else {
          next.add(rowId);
        }
        return next;
      });
    }
  }, [expandable?.accordion]);

  // Reset expanded rows when data changes
  useEffect(() => {
    setExpandedRowKeys(new Set());
  }, [data]);

  // Use custom hooks for table logic
  const { effectiveColumns, minTableWidth: computedMinTableWidth } = useTableColumns({
    columns,
    actions,
    actionsHeader,
    selectable,
  });
  const minTableWidth = minTableWidthOverride ?? computedMinTableWidth;

  const effectiveInitialSort = useMemo(() => {
    if (initialSort) return initialSort;
    const firstSortableColumn = columns.find((col) => col.sortable) ?? columns[0];
    if (!firstSortableColumn) return undefined;
    return { key: String(firstSortableColumn.key), direction: 'asc' as SortDirection };
  }, [initialSort, columns]);

  const { sortedData, sort, handleSort } = useTableSort({
    data,
    columns: effectiveColumns,
    initialSort: effectiveInitialSort,
    serverSideSort,
    onSortChange,
    isLoading,
  });

  // If no server-side/onSearch is provided, apply client-side filtering
  const visibleData = useMemo(() => {
    // If caller provided onSearch (server-side) or there's no query, don't filter locally
    if (onSearch) return sortedData;
    const q = String(searchValue || '').trim().toLowerCase();
    if (!q) return sortedData;

    // Helper to get nested values (support dot paths like 'user.name')
    const getValueByKey = (row: T, key: string): any => {
      if (!key) return undefined;
      if (key.includes('.')) {
        return key.split('.').reduce((acc: any, part: string) => (acc ? acc[part] : undefined), row);
      }
      return (row as any)[key];
    };

    // Collect keys from effective columns to search against
    const keys = effectiveColumns.map((c) => String(c.key));

    return sortedData.filter((row) =>
      keys.some((key) => {
        const value = getValueByKey(row, key);
        if (value === null || value === undefined) return false;
        if (typeof value === 'string') return value.toLowerCase().includes(q);
        if (typeof value === 'number' || typeof value === 'bigint') return String(value).toLowerCase().includes(q);
        if (value instanceof Date) return value.toISOString().toLowerCase().includes(q);
        try {
          const serialized = String(value);
          return serialized.toLowerCase().includes(q);
        } catch {
          return false;
        }
      }),
    );
  }, [sortedData, searchValue, onSearch, effectiveColumns]);

  const {
    selectedIds, allVisibleSelected, isIndeterminate, hasSelectableRows, toggleSelectAll, toggleRow,
  } = useTableSelection({
    data,
    getRowId: effectiveGetRowId,
    selectable,
    onSelectionChange,
    isLoading,
    isRowSelectable,
    selectedRowIds,
  });

  const visibleSelectableCount = useMemo(
    () => (isRowSelectable ? visibleData.filter(isRowSelectable).length : visibleData.length),
    [visibleData, isRowSelectable],
  );

  const effectiveSelectAllTotal = selectAllTotalCount ?? totalRows;
  const hasSelection = selectedIds.size > 0 || isSelectAllTotalActive;
  const hasMorePages = selectable && effectiveSelectAllTotal > visibleSelectableCount;
  const canSelectAllTotal = hasMorePages && Boolean(onSelectAllTotal);
  const deselectedFromTotal = isSelectAllTotalActive
    ? Math.max(visibleSelectableCount - selectedIds.size, 0)
    : 0;
  const selectedCount = isSelectAllTotalActive
    ? effectiveSelectAllTotal - deselectedFromTotal
    : selectedIds.size;

  // Always show expand column if expandable config is provided
  // Individual rows will be disabled based on rowExpandable function
  const effectiveExpandable = expandable;

  // Body key for forcing remount on page change
  const bodyKey = useMemo(() => {
    if (pagination) {
      return `p:${pagination.page}-s:${pagination.pageSize}`;
    }
    return `len:${data.length}`;
  }, [pagination, data.length]);

  return (
    <Card
      elevation={1}
      padding="16px 0"
      className={`flex flex-col gap-4 relative ${className}`}
      style={{
        ...style,
        ['--row-hover' as any]: TABLE_PALETTE.rowHover,
      }}
      data-testid={dataTestId}
      borderRadius="24px"
    >
      <TableHeader
        searchable={searchable}
        searchValue={searchValue}
        placeholder={searchPlaceholder}
        onSearchChange={handleSearchChange}
        onEnterSearch={onEnterSearch}
      />
      <TableContainer
        columns={effectiveColumns}
        selectable={selectable}
        allVisibleSelected={canSelectAllTotal ? isSelectAllTotalActive : allVisibleSelected}
        onToggleSelectAll={() => {
          if (isLoading) return;
          if (isSelectAllTotalActive) {
            onClearSelectionTotal?.();
          } else if (canSelectAllTotal) {
            // Hay más filas que cumplen los filtros de las que se ven en esta página: un solo
            // clic en el checkbox del encabezado va directo a la selección total (sin el paso
            // intermedio de "solo esta página", que quedaba oculto detrás de un segundo clic
            // sobre un checkbox que ya se veía marcado/indeterminado — confuso para el usuario).
            // También se marcan como seleccionadas las filas visibles (si no lo estaban ya): el
            // checkbox de cada fila y `onSelectionChange` deben reflejar la selección real, ya
            // que el hook de dominio usa esa lista para calcular qué filas de esta página se
            // excluyen manualmente (`excludeIncidentIds`) — sin esto, todas las filas visibles
            // se habrían visto como "deseleccionadas" pese a estar incluidas en el total.
            if (!allVisibleSelected) toggleSelectAll();
            onSelectAllTotal?.();
          } else {
            toggleSelectAll();
          }
        }}
        onSort={handleSort}
        sort={sort}
        disabled={isLoading}
        minTableWidth={minTableWidth}
        fillHeight={!isLoading && visibleData.length === 0}
        expandable={effectiveExpandable}
        hasSelection={hasSelection}
        bulkActions={bulkActions}
        // Habilitado si esta página tiene filas seleccionables, o si hay más filas
        // seleccionables entre TODOS los resultados filtrados aunque esta página no tenga
        // ninguna visible (ej. página llena de "Aprobado" con pendientes en otras páginas) —
        // en ese caso un clic activa directo la selección total (ver canSelectAllTotal arriba).
        selectAllDisabled={!hasSelectableRows && !canSelectAllTotal}
        isIndeterminate={
          isIndeterminate || (allVisibleSelected && canSelectAllTotal && !isSelectAllTotalActive)
        }
        fontSize={fontSize}
      >
        {isLoading ? (
          <TableSkeleton
            columns={effectiveColumns}
            selectable={selectable}
            expandable={effectiveExpandable}
          />
        ) : (
          <TableBody
            key={bodyKey}
            data={visibleData}
            columns={effectiveColumns}
            selectable={selectable}
            selectedIds={selectedIds}
            toggleRow={toggleRow}
            getRowId={effectiveGetRowId}
            disabled={isLoading}
            expandable={effectiveExpandable}
            expandedRowKeys={expandedRowKeys}
            toggleRowExpanded={toggleRowExpanded}
            emptyMessage={emptyMessage}
            isRowSelectable={isRowSelectable}
            fontSize={fontSize}
            getRowTextColor={getRowTextColor}
          />
        )}
      </TableContainer>
      <TableFooter
        pageInfo={
          pagination
            ? {
              current: pagination.page,
              pageSize: pagination.pageSize,
              total: pagination.total,
            }
            : null
        }
        onChangePage={isLoading ? undefined : pagination?.onChange}
        onChangePageSize={isLoading ? undefined : pagination?.onPageSizeChange}
        pageSizeOptions={pagination?.pageSizeOptions}
        totalRows={totalRows}
        disabled={isLoading}
        hideTotalRows={hideTotalRows}
        selectedCount={selectedCount}
      />
      {footerNote && (
        <div className="px-4" data-testid="table-footer-note">
          {footerNote}
        </div>
      )}
    </Card>
  );
}

export default Table;
